import React, { useMemo, useState, useEffect } from 'react';

const MONTHS_DE = [
  'Januar', 'Februar', 'März', 'April', 'Mai', 'Juni',
  'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember',
];

const WEEKDAYS_DE = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'];

/** Festes Ankerjahr nur für Kalenderberechnung (Schaltjahr), nicht Teil der Auswahl */
const CAL_YEAR = 2000;

function pad(n) {
  return String(n).padStart(2, '0');
}

/** Wert im State/API: MM-DD */
function parseMD(s) {
  if (!s) return null;
  const t = s.includes('T') ? s.split('T')[0] : s;
  const parts = t.split('-').map(Number);
  let m;
  let d;
  if (parts.length === 2) {
    [m, d] = parts;
  } else if (parts.length === 3) {
    [, m, d] = parts;
  } else {
    return null;
  }
  if (!m || !d || m < 1 || m > 12) return null;
  return new Date(CAL_YEAR, m - 1, d);
}

function toMD(monthIndex, day) {
  return `${pad(monthIndex + 1)}-${pad(day)}`;
}

/** Kalender-Modal: nur Tag & Monat, ohne Jahreszahl */
const BirthDateModal = ({ isOpen, value, onSelect, onClose }) => {
  const initial = parseMD(value);
  const [viewMonth, setViewMonth] = useState(initial ? initial.getMonth() : new Date().getMonth());

  useEffect(() => {
    if (isOpen) {
      const d = parseMD(value);
      setViewMonth(d ? d.getMonth() : new Date().getMonth());
    }
  }, [isOpen, value]);

  const { days, leadingEmpty } = useMemo(() => {
    const first = new Date(CAL_YEAR, viewMonth, 1);
    const last = new Date(CAL_YEAR, viewMonth + 1, 0);
    const startPad = (first.getDay() + 6) % 7;
    const cells = [];
    for (let i = 1; i <= last.getDate(); i++) {
      cells.push(i);
    }
    return { days: cells, leadingEmpty: startPad };
  }, [viewMonth]);

  if (!isOpen) return null;

  const selected = parseMD(value);
  const selMonth = selected ? selected.getMonth() : null;
  const selDay = selected ? selected.getDate() : null;

  const handleDayClick = (day) => {
    onSelect(toMD(viewMonth, day));
    onClose();
  };

  const today = new Date();
  const isTodayCell = (day) =>
    viewMonth === today.getMonth() && day === today.getDate();

  return (
    <div
      className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label="Geburtstag wählen (Tag und Monat)"
    >
      <div
        className="bg-accent-3 rounded-xl border-2 border-accent-1/40 shadow-xl w-full max-w-sm overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="bg-accent-3 border-b border-accent-1/30 px-4 py-3 flex items-center justify-between">
          <button
            type="button"
            onClick={() => setViewMonth((m) => (m === 0 ? 11 : m - 1))}
            className="text-bg-1 hover:bg-accent-1/20 p-1 rounded-lg text-lg font-bold transition-colors"
            aria-label="Vorheriger Monat"
          >
            ‹
          </button>
          <span className="font-heading font-bold text-bg-1 text-center flex-1">
            {MONTHS_DE[viewMonth]}
          </span>
          <button
            type="button"
            onClick={() => setViewMonth((m) => (m === 11 ? 0 : m + 1))}
            className="text-bg-1 hover:bg-accent-1/20 p-1 rounded-lg text-lg font-bold transition-colors"
            aria-label="Nächster Monat"
          >
            ›
          </button>
        </div>
        <div className="p-3 bg-accent-3">
          <div className="grid grid-cols-7 gap-1 mb-2">
            {WEEKDAYS_DE.map((w) => (
              <div key={w} className="text-center text-xs font-semibold text-bg-1/80 py-1">
                {w}
              </div>
            ))}
          </div>
          <div className="grid grid-cols-7 gap-1">
            {Array.from({ length: leadingEmpty }).map((_, i) => (
              <div key={`e-${i}`} className="aspect-square" />
            ))}
            {days.map((day) => {
              const isSel = selMonth === viewMonth && selDay === day;
              const todayCell = isTodayCell(day);
              return (
                <button
                  key={day}
                  type="button"
                  onClick={() => handleDayClick(day)}
                  className={`aspect-square rounded-lg text-sm font-medium transition-colors
                    ${isSel ? 'bg-accent-1 text-white shadow-md' : 'text-bg-1 bg-white/50 hover:bg-accent-2/30'}
                    ${todayCell && !isSel ? 'ring-2 ring-accent-1 ring-inset' : ''}
                  `}
                >
                  {day}
                </button>
              );
            })}
          </div>
        </div>
        <div className="px-3 pb-3 flex gap-2 bg-accent-3">
          <button
            type="button"
            onClick={() => {
              onSelect('');
              onClose();
            }}
            className="flex-1 py-2.5 rounded-lg border-2 border-accent-1/40 text-bg-1 text-sm font-medium hover:bg-white/60"
          >
            Kein Datum
          </button>
          <button
            type="button"
            onClick={onClose}
            className="flex-1 py-2.5 rounded-lg bg-white border-2 border-accent-1/40 text-bg-1 text-sm font-medium hover:bg-accent-2/30"
          >
            Abbrechen
          </button>
        </div>
      </div>
    </div>
  );
};

export default BirthDateModal;
